import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { TextInput } from '#src/atoms/inputs/TextInput/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';

const CUSTOM_VALUE = '__custom__';

const StyledNativeSelect = styled(NativeSelect)`
  width: 100%;
  background-color: var(--c-black);
  border-color: var(--c-nd-200);
`;

const StyledTextInput = styled(TextInput)`
  background-color: var(--c-black);
`;

type Option = {
  value: string;
  label: string;
};

type Props = {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  min?: number;
  max?: number;
  unit?: string;
};

export const SelectWithCustomInput = ({
  options,
  value,
  onChange,
  min = 1,
  max,
  unit,
}: Props) => {
  const { formatMessage } = useIntl();
  const [isCustom, setIsCustom] = useState(
    () => value !== '' && !options.some(o => o.value === value)
  );

  const selectOptions = [
    ...options,
    {
      value: CUSTOM_VALUE,
      label: formatMessage({
        defaultMessage: 'Custom',
        description:
          'Option in a select that lets the user enter a custom value',
      }),
    },
  ];

  const handleSelectChange = (selected: string) => {
    if (selected === CUSTOM_VALUE) {
      setIsCustom(true);
      onChange('');
    } else {
      setIsCustom(false);
      onChange(selected);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange('');
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    if (max !== undefined && num > max) {
      onChange(String(max));
      return;
    }
    onChange(raw);
  };

  return (
    <Vertical gap={1}>
      <StyledNativeSelect
        values={selectOptions}
        value={isCustom ? CUSTOM_VALUE : value}
        onChange={handleSelectChange}
        placeholder={formatMessage({ defaultMessage: 'Select' })}
        fullWidth
      />
      {!!isCustom && (
        <StyledTextInput
          type="number"
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          placeholder={
            unit
              ? formatMessage(
                  { defaultMessage: 'Enter value ({unit})' },
                  { unit }
                )
              : formatMessage({ defaultMessage: 'Enter value' })
          }
          aria-label={formatMessage({ defaultMessage: 'Custom value' })}
          autoFocus
        />
      )}
    </Vertical>
  );
};
